import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, FileTextIcon } from 'lucide-react';
interface TermsScreenProps {
  onBack: () => void;
  onAccept: () => void;
}
export function TermsScreen({
  onBack,
  onAccept
}: TermsScreenProps) {
  const [language, setLanguage] = useState<'en' | 'ur'>('en');
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full min-h-screen flex flex-col py-6 px-6" style={{
    backgroundColor: '#FFF5F0'
  }} data-id="element-1000">
      {/* Header */}
      <div className="flex items-center justify-between mb-8" data-id="element-1001">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-black/5 transition-colors" data-id="element-1002">
          <ArrowLeftIcon className="w-6 h-6 text-[#1A1A1A]" strokeWidth={2} data-id="element-1003" />
        </button>
        <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-8 w-auto" data-id="element-1004" />
        <div className="w-10" data-id="element-1005" />
      </div>

      {/* Title */}
      <div className="mb-6" data-id="element-1006">
        <div className="w-12 h-12 rounded-full bg-[#FCECE3] flex items-center justify-center mb-4" data-id="element-1007">
          <FileTextIcon className="w-6 h-6 text-[#D95A1A]" strokeWidth={2} data-id="element-1008" />
        </div>
        <h1 className="text-[32px] font-bold text-[#1A1A1A] leading-tight mb-1 tracking-tight" data-id="element-1009">
          Terms & Conditions
        </h1>
        <p className="text-[15px] text-[#C85A1A]" data-id="element-1010">
          Please read and accept to continue
        </p>
      </div>

      {/* Language Tabs */}
      <div className="flex gap-2 mb-4" data-id="element-1011">
        <button onClick={() => setLanguage('en')} className={`flex-1 py-3 rounded-xl text-[14px] font-bold transition-all ${language === 'en' ? 'bg-[#661806] text-white shadow-md' : 'bg-[#F5EBE6] text-[#818285]'}`} data-id="element-1012">
          English
        </button>
        <button onClick={() => setLanguage('ur')} className={`flex-1 py-3 rounded-xl text-[14px] font-bold transition-all ${language === 'ur' ? 'bg-[#661806] text-white shadow-md' : 'bg-[#F5EBE6] text-[#818285]'}`} data-id="element-1013">
          اردو
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 bg-white rounded-3xl p-6 shadow-sm overflow-y-auto mb-6 border border-[#FCECE3]" data-id="element-1014">
        <AnimatePresence mode="wait" data-id="element-1015">
          {language === 'en' ? <motion.div key="en" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="space-y-4 text-[14px] text-[#4A3B32] leading-relaxed" data-id="element-1016">
              <p className="font-bold text-[#1A1A1A]" data-id="element-1017">1. Acceptance of Terms</p>
              <p data-id="element-1018">
                By accessing and using the digiBOP mobile banking application,
                you agree to be bound by these Terms and Conditions.
              </p>

              <p className="font-bold text-[#1A1A1A] mt-4" data-id="element-1019">
                2. User Registration
              </p>
              <p data-id="element-1020">
                You must provide accurate and complete information during the
                registration process, including your valid CNIC.
              </p>

              <p className="font-bold text-[#1A1A1A] mt-4" data-id="element-1021">
                3. Account Security
              </p>
              <p data-id="element-1022">
                You are responsible for maintaining the confidentiality of your
                MPIN and account details. The bank will never ask for your MPIN
                or OTP.
              </p>

              <p className="font-bold text-[#1A1A1A] mt-4" data-id="element-1023">4. Transactions</p>
              <p data-id="element-1024">
                All transactions processed through the app are subject to
                verification and available balance. Standard limits and charges
                apply as per the bank's schedule of charges.
              </p>
            </motion.div> : <motion.div key="ur" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="space-y-4 text-[15px] text-[#4A3B32] leading-relaxed text-right font-urdu" dir="rtl" data-id="element-1025">
              <p className="font-bold text-[#1A1A1A]" data-id="element-1026">1. شرائط کی منظوری</p>
              <p data-id="element-1027">
                ڈیجی بی او پی موبائل بینکنگ ایپلی کیشن تک رسائی اور استعمال کر
                کے، آپ ان شرائط و ضوابط کے پابند ہونے سے اتفاق کرتے ہیں۔
              </p>

              <p className="font-bold text-[#1A1A1A] mt-4" data-id="element-1028">
                2. صارف کی رجسٹریشن
              </p>
              <p data-id="element-1029">
                آپ کو رجسٹریشن کے عمل کے دوران درست اور مکمل معلومات فراہم کرنی
                ہوں گی، بشمول آپ کا درست شناختی کارڈ نمبر۔
              </p>

              <p className="font-bold text-[#1A1A1A] mt-4" data-id="element-1030">
                3. اکاؤنٹ کی سیکیورٹی
              </p>
              <p data-id="element-1031">
                آپ اپنے ایم پن اور اکاؤنٹ کی تفصیلات کی رازداری برقرار رکھنے کے
                ذمہ دار ہیں۔ بینک کبھی بھی آپ کا ایم پن یا او ٹی پی نہیں مانگے
                گا۔
              </p>

              <p className="font-bold text-[#1A1A1A] mt-4" data-id="element-1032">4. لین دین</p>
              <p data-id="element-1033">
                ایپ کے ذریعے کارروائی کی جانے والی تمام ٹرانزیکشنز تصدیق اور
                دستیاب بیلنس سے مشروط ہیں۔ بینک کے چارجز کے شیڈول کے مطابق
                معیاری حدود اور چارجز لاگو ہوں گے۔
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Bottom Section */}
      <div className="pb-4" data-id="element-1034">
        <button onClick={onAccept} className="w-full bg-gradient-to-r from-[#D95A1A] to-[#B84A12] text-white py-4 rounded-full font-bold text-[15px] tracking-wide shadow-[0_8px_20px_rgba(217,90,26,0.3)] hover:shadow-[0_8px_25px_rgba(217,90,26,0.4)] active:scale-[0.98] transition-all" data-id="element-1035">
          ACCEPT & CONTINUE
        </button>
      </div>
    </motion.div>;
}